// Funnel v2 design atoms — "operator's dossier" system.
//
// Discipline rules the conversion design here:
//   · coral appears ONCE per screen — the primary action (plus the key price)
//   · teal is reserved for assurance marks (checks, promises, "saved" states)
//   · money and meta-labels are set in mono — receipt/ledger register
//   · hairline borders (white/8–12), generous space, no shouting

import type { ButtonHTMLAttributes, ComponentType, ReactNode } from 'react';
import { Link as RouterDomLink } from 'react-router-dom';
import { ArrowRight, Check, ShieldCheck } from 'lucide-react';

// react-router-dom resolves its own @types/react copy whose ReactNode lacks
// bigint; realign to this package's React types so children pass cleanly.
const Link = RouterDomLink as unknown as ComponentType<{
  to: string;
  className?: string;
  children?: ReactNode;
}>;

const PRIMARY_CLASSES =
  'group inline-flex items-center justify-center gap-2.5 rounded-md bg-brand-coral px-7 py-4 font-display text-[15px] font-semibold tracking-tight text-white shadow-[0_18px_48px_rgba(254,89,99,0.32)] transition hover:-translate-y-0.5 hover:bg-[#ff6b72] focus:outline-none focus:ring-2 focus:ring-brand-coral/70 focus:ring-offset-2 focus:ring-offset-[#080a0f] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0';

export function PrimaryCtaLink({ to, children, className = '' }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={`${PRIMARY_CLASSES} ${className}`}>
      {children}
      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
    </Link>
  );
}

export function PrimaryCtaButton({
  children,
  className = '',
  withArrow = true,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { withArrow?: boolean }) {
  return (
    <button {...rest} className={`${PRIMARY_CLASSES} ${className}`}>
      {children}
      {withArrow ? <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /> : null}
    </button>
  );
}

export function QuietCtaButton({ children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={`inline-flex w-full items-center justify-center rounded-md border border-white/12 bg-white/[0.03] px-6 py-3.5 text-sm font-medium text-slate-300 transition hover:border-white/25 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 ${className}`}
    >
      {children}
    </button>
  );
}

/** Mono uppercase eyebrow — the dossier's section label register. */
export function Kicker({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p className={`font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500 ${className}`}>
      {children}
    </p>
  );
}

/** Numbered editorial section marker: `01 — The bottleneck`. */
export function SectionMark({ no, label }: { no: string; label: string }) {
  return (
    <div className="mb-6 flex items-baseline gap-3">
      <span className="font-mono text-sm font-semibold text-brand-teal/80">{no}</span>
      <span className="h-px flex-none w-8 self-center bg-white/15" aria-hidden="true" />
      <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</span>
    </div>
  );
}

export function DisplayHeading({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <h2 className={`max-w-3xl font-display text-3xl font-semibold leading-[1.06] tracking-tight text-white md:text-5xl ${className}`}>
      {children}
    </h2>
  );
}

/** A receipt-style line item. Use inside a LedgerCard. */
export function LedgerRow({
  label,
  sub,
  amount,
  muted = false,
  accent = false,
}: {
  label: ReactNode;
  sub?: ReactNode;
  amount: ReactNode;
  muted?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dashed border-white/10 py-3 last:border-b-0">
      <div className="min-w-0">
        <p className={`text-sm font-medium ${muted ? 'text-slate-500' : 'text-white'}`}>{label}</p>
        {sub ? <p className="mt-0.5 text-xs leading-5 text-slate-500">{sub}</p> : null}
      </div>
      <p className={`shrink-0 font-mono text-sm font-semibold tabular-nums ${accent ? 'text-brand-coral' : muted ? 'text-slate-500' : 'text-slate-200'}`}>
        {amount}
      </p>
    </div>
  );
}

export function LedgerCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-white/10 bg-[#0e131b] p-1.5 shadow-2xl shadow-black/40 ${className}`}>
      <div className="rounded-md border border-white/10 bg-[#090c12] p-5 md:p-6">{children}</div>
    </div>
  );
}

export function AssuranceItem({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2.5 text-sm leading-6 text-slate-300">
      <Check className="mt-1 h-4 w-4 shrink-0 text-brand-teal" />
      <span>{children}</span>
    </div>
  );
}

/** The 14-day Value Promise, framed as upside — never as a refund window. */
export function ValuePromise({ className = '' }: { className?: string }) {
  return (
    <div className={`flex gap-3 rounded-md border border-brand-teal/20 bg-brand-teal/[0.06] p-4 ${className}`}>
      <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-teal" />
      <div>
        <p className="text-sm font-semibold text-white">14-day Value Promise</p>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          If your first 14 days do not deliver the clarity we promised, tell us. We will work with
          you until you have what you came for — more access, a working session, help building it.
        </p>
      </div>
    </div>
  );
}

/** Blueprint-grid backdrop for hero sections, masked so it fades out. */
export function BlueprintGrid() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(93,217,209,0.05)_1px,transparent_1px),linear-gradient(rgba(93,217,209,0.045)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,black,transparent)]"
    />
  );
}
